import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/700.css";
import {
    ShieldCheck, Landmark, FileWarning, Bug, Brain, GitBranch, KanbanSquare,
    FileText, Mail, Users, Lock, Cog, Fingerprint, Eye, FileCheck2, Shield,
    ArrowRight, Menu, X, ChevronRight,
} from "lucide-react";

// Cybersecurity enterprise AI-generated mockups (dark theme)
const IMG_HERO_RADAR = "https://static.prod-images.emergentagent.com/jobs/e739d394-39e1-45c1-aee1-25b88163454e/images/15820dbb7d8437203d8a8d8478a18869e046d1a4116db90cfd46270bd289d302.jpeg";
const IMG_DARK_DASHBOARD = "https://static.prod-images.emergentagent.com/jobs/e739d394-39e1-45c1-aee1-25b88163454e/images/d9c32e34adfe80792780bbc1c6d187d0a41c89b61e956bf80630c9bd2691e9d0.jpeg";
const IMG_PEC_ANALYSIS = "https://static.prod-images.emergentagent.com/jobs/e739d394-39e1-45c1-aee1-25b88163454e/images/aeddf3d0e26863b87147f50f8cb31511c3debea4a8e910adf9df5ddb89c8dea3.jpeg";
const IMG_NETWORK_FLOW = "https://static.prod-images.emergentagent.com/jobs/e739d394-39e1-45c1-aee1-25b88163454e/images/81985e7f53f17995545355f68c8d015b7e8400962d18d2976df7be74ce32ff65.jpeg";

const DEMO_MAILTO = "mailto:andrea.bazzarello@ptkserv.it?subject=" +
    encodeURIComponent("Richiesta demo IH PEC Guard") +
    "&body=" +
    encodeURIComponent("Salve Andrea,\n\nsono interessato a una demo di IH PEC Guard. I miei contatti:\n\nNome: \nAzienda: \nTelefono: \n\nGrazie.");

// Reusable classnames
const MONO = "font-mono tracking-widest";
const EYEBROW = "inline-block text-[11px] uppercase tracking-[0.3em] font-mono text-cyan-400";

// Dot grid background (SVG data URI)
const DOT_GRID_STYLE = {
    backgroundImage:
        "radial-gradient(rgba(56,189,248,0.08) 1px, transparent 1px)",
    backgroundSize: "24px 24px",
};

// Custom hook: fade-in on scroll (IntersectionObserver)
function useRevealOnScroll() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    e.target.classList.add("landing-revealed");
                    io.unobserve(e.target);
                }
            });
        }, { threshold: 0.08, rootMargin: "0px 0px -60px 0px" });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return ref;
}

// Tactical corner accents (4 L-shape corners) for images
function CornerAccents({ color = "rgba(56,189,248,0.55)" }) {
    const c = { position: "absolute", width: 22, height: 22, borderColor: color };
    return (
        <>
            <span style={{ ...c, top: -2, left: -2, borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}` }} aria-hidden />
            <span style={{ ...c, top: -2, right: -2, borderTop: `2px solid ${color}`, borderRight: `2px solid ${color}` }} aria-hidden />
            <span style={{ ...c, bottom: -2, left: -2, borderBottom: `2px solid ${color}`, borderLeft: `2px solid ${color}` }} aria-hidden />
            <span style={{ ...c, bottom: -2, right: -2, borderBottom: `2px solid ${color}`, borderRight: `2px solid ${color}` }} aria-hidden />
        </>
    );
}

function LiveDot() {
    return (
        <span className="inline-flex items-center gap-2 text-xs text-emerald-300" data-testid="live-status">
            <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            <span className={`${MONO} text-[10px]`}>SISTEMA OPERATIVO</span>
        </span>
    );
}

function NavBar() {
    const [open, setOpen] = React.useState(false);
    const items = [
        { href: "#funzionalita", label: "Funzionalità" },
        { href: "#come-funziona", label: "Come funziona" },
        { href: "#trust", label: "Trust" },
        { href: "#contatti", label: "Contatti" },
    ];
    return (
        <header
            data-testid="landing-navbar"
            className="sticky top-0 z-50 bg-[#0B1633]/95 backdrop-blur-xl border-b border-cyan-400/15"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
                <a href="#top" className="flex items-center gap-2.5" data-testid="landing-logo">
                    <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-cyan-400" strokeWidth={2.25} />
                    </div>
                    <span className="text-[15px] font-bold text-white tracking-tight">IH PEC Guard</span>
                </a>
                <nav className="hidden md:flex items-center gap-8">
                    {items.map((i) => (
                        <a key={i.href} href={i.href}
                            data-testid={`landing-nav-${i.href.slice(1)}`}
                            className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">
                            {i.label}
                        </a>
                    ))}
                </nav>
                <div className="hidden md:flex items-center gap-5">
                    <LiveDot />
                    <Link
                        to="/login"
                        data-testid="landing-login-cta-top"
                        className="text-sm px-4 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold hover:shadow-[0_0_24px_rgba(56,189,248,0.35)] transition-all"
                    >
                        Accedi
                    </Link>
                </div>
                <button
                    className="md:hidden p-2 rounded-md text-white hover:bg-white/5"
                    onClick={() => setOpen(!open)}
                    data-testid="landing-mobile-menu-btn"
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>
            {open && (
                <div className="md:hidden border-t border-cyan-400/15 bg-[#0B1633] px-5 py-3 space-y-2" data-testid="landing-mobile-menu">
                    {items.map((i) => (
                        <a key={i.href} href={i.href} onClick={() => setOpen(false)}
                            className="block text-sm text-slate-200 py-2">
                            {i.label}
                        </a>
                    ))}
                    <Link to="/login" onClick={() => setOpen(false)}
                        className="block text-sm px-4 py-2 rounded-lg bg-[#2563EB] text-white text-center font-semibold">
                        Accedi
                    </Link>
                </div>
            )}
        </header>
    );
}

function Hero() {
    const ref = useRevealOnScroll();
    return (
        <section id="top" ref={ref} className="reveal relative overflow-hidden" data-testid="landing-hero">
            <div aria-hidden className="absolute inset-0" style={DOT_GRID_STYLE} />
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(56,189,248,0.18), transparent 60%)" }}
            />
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center min-h-[90vh] relative">
                <div>
                    <div className={EYEBROW} data-testid="hero-eyebrow">
                        [ PEC · SECURITY · ENTERPRISE ]
                    </div>
                    <h1 className="mt-6 text-5xl md:text-7xl font-black text-white leading-[1.03] tracking-tight">
                        La difesa che <br />
                        <span className="text-cyan-400">vede oltre</span> <br />
                        la firma.
                    </h1>
                    <p className="mt-7 text-lg text-slate-300 leading-relaxed max-w-xl">
                        IH PEC Guard analizza ogni PEC su 5 dimensioni indipendenti per identificare frodi,
                        phishing e richieste sospette, anche quando la firma tecnica è valida.
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/login"
                            data-testid="hero-login-cta"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold transition-all hover:shadow-[0_0_40px_rgba(37,99,235,0.55)]"
                        >
                            Accedi alla piattaforma
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href={DEMO_MAILTO}
                            data-testid="hero-demo-cta"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-cyan-400/40 text-cyan-100 hover:bg-cyan-400/10 hover:border-cyan-400 font-semibold transition-all"
                        >
                            Richiedi demo
                        </a>
                    </div>
                    <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-2 max-w-lg">
                        {[
                            "Trust List AgID sincronizzata",
                            "Verifica S/MIME reale",
                            "Isolamento tenant garantito",
                            "GDPR compliant by design",
                        ].map((t) => (
                            <li key={t} className={`${MONO} text-[10px] uppercase text-slate-400 flex items-center gap-1.5`}>
                                <span className="text-cyan-400">&gt;</span>
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative">
                    <div className="relative animate-float">
                        <img
                            src={IMG_HERO_RADAR}
                            alt="Radar shield PEC security"
                            data-testid="hero-mockup"
                            className="rounded-2xl w-full ring-1 ring-cyan-400/15 shadow-[0_30px_100px_rgba(6,16,42,0.7)]"
                            loading="eager"
                        />
                        <CornerAccents />
                    </div>
                    <div className="mt-8 grid grid-cols-3 divide-x divide-cyan-400/15 border-y border-cyan-400/15 py-4"
                        data-testid="hero-metrics">
                        {[
                            { n: "54", l: "PROVIDER PEC ACCREDITATI" },
                            { n: "212", l: "CERTIFICATI AGID" },
                            { n: "5", l: "DIMENSIONI DI RISCHIO" },
                        ].map((m, i) => (
                            <div key={i} className={`px-3 text-center ${i === 0 ? "pl-0" : ""} ${i === 2 ? "pr-0" : ""}`}>
                                <div className={`${MONO} text-3xl md:text-4xl font-bold text-cyan-400`}>{m.n}</div>
                                <div className={`${MONO} text-[9px] uppercase text-slate-500 mt-1`}>{m.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function ProblemSection() {
    const ref = useRevealOnScroll();
    const cards = [
        { stat: "€ 1M+", icon: Landmark, title: "Frodi bancarie",
            text: "Richieste di modifica IBAN da mittenti compromessi. Il conto di destinazione cambia in silenzio." },
        { stat: "73%", icon: FileWarning, title: "Fatture false",
            text: "Documenti falsi allegati a PEC valide, pronti al pagamento senza verifica manuale." },
        { stat: "48h", icon: Bug, title: "Phishing avanzato",
            text: "Impersonazione di Agenzia delle Entrate, avvocati e fornitori con dettagli plausibili." },
    ];
    return (
        <section ref={ref} className="reveal bg-[#0B1633] py-20 md:py-28 relative" data-testid="section-problem">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className={EYEBROW}>[ IL PROBLEMA ]</div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                        Una PEC valida <span className="text-cyan-400">non</span> è una PEC sicura
                    </h2>
                    <p className="mt-5 text-base text-slate-400">
                        Ogni giorno arrivano PEC tecnicamente autentiche che nascondono:
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-5">
                    {cards.map((c, i) => (
                        <div key={i}
                            data-testid={`problem-card-${i}`}
                            className="landing-card group relative rounded-xl bg-[#142340] border border-cyan-400/15 p-7 hover:border-red-500/40 transition-all"
                        >
                            <div className="w-14 h-14 rounded-lg mb-5 flex items-center justify-center"
                                style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(56,189,248,0.15))" }}>
                                <c.icon className="w-6 h-6 text-cyan-300" strokeWidth={2} />
                            </div>
                            <div className={`${MONO} text-3xl md:text-4xl font-bold text-red-400 mb-3`} data-testid={`problem-stat-${i}`}>
                                {c.stat}
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">{c.title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
                            <div className={`${MONO} text-[9px] text-slate-600 mt-4 uppercase`}>[ esempio scenario ]</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function DimensionRow({ n, name, desc, meter, color }) {
    return (
        <div className="grid grid-cols-[52px_1fr] gap-4 py-5 border-b border-cyan-400/10 last:border-b-0">
            <div className={`${MONO} text-3xl font-bold text-cyan-400/80`}>{n}</div>
            <div>
                <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-[15px] font-bold text-white">{name}</h3>
                    <span className={`${MONO} text-[10px] uppercase ${color}`}>meter</span>
                </div>
                <p className="text-sm text-slate-400 mt-1 leading-relaxed">{desc}</p>
                <div className="mt-3 relative">
                    <div className="h-2 rounded-full overflow-hidden bg-white/5"
                        style={{ background: "linear-gradient(90deg, rgba(22,163,74,0.35) 0%, rgba(245,158,11,0.35) 55%, rgba(220,38,38,0.4) 100%)" }} />
                    <div className="absolute -top-1" style={{ left: `${meter}%` }}>
                        <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[9px] border-l-transparent border-r-transparent border-b-cyan-300 drop-shadow" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function DimensionsSection() {
    const ref = useRevealOnScroll();
    const dims = [
        { n: "01", name: "Autenticità tecnica",
            desc: "Busta di trasporto, daticert.xml, catena S/MIME, Trust List AgID.",
            meter: 12, color: "text-emerald-300" },
        { n: "02", name: "Affidabilità mittente",
            desc: "Anagrafica fornitori, storico comunicazioni, typosquatting detection.",
            meter: 28, color: "text-cyan-300" },
        { n: "03", name: "Rischio contenuto",
            desc: "Analisi AI su intent, urgenze, richieste anomale, IBAN citati.",
            meter: 55, color: "text-amber-300" },
        { n: "04", name: "Rischio allegati e link",
            desc: "Macro Office, doppie estensioni, punycode, URL shortener, IP literal.",
            meter: 72, color: "text-orange-300" },
        { n: "05", name: "Rischio operativo",
            desc: "Score composito + raccomandazione operativa in linguaggio umano.",
            meter: 88, color: "text-red-300" },
    ];
    return (
        <section id="come-funziona" ref={ref} className="reveal bg-[#06102A] py-20 md:py-28 relative" data-testid="section-dimensions">
            <div aria-hidden className="absolute inset-0" style={DOT_GRID_STYLE} />
            <div className="max-w-6xl mx-auto px-5 md:px-8 relative">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className={EYEBROW}>[ 5 DIMENSIONI · UN SOLO RISULTATO ]</div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                        Analisi multi-dimensionale.<br />
                        <span className="text-cyan-400">Nessun punteggio opaco.</span>
                    </h2>
                    <p className="mt-5 text-base text-slate-400 max-w-2xl mx-auto">
                        Ogni PEC riceve 5 valutazioni separate con spiegazione dei fattori.
                        La verità non è mai un solo numero.
                    </p>
                </div>
                <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 items-center">
                    <div className="rounded-2xl border border-cyan-400/15 bg-[#0B1633]/60 px-6 md:px-8 py-2" data-testid="dimensions-meters">
                        {dims.map((d) => (
                            <DimensionRow key={d.n} {...d} />
                        ))}
                    </div>
                    <div className="relative">
                        <img
                            src={IMG_PEC_ANALYSIS}
                            alt="Analisi PEC su 5 dimensioni"
                            data-testid="dimensions-mockup"
                            className="rounded-2xl w-full ring-1 ring-cyan-400/15 shadow-[0_30px_100px_rgba(6,16,42,0.7)]"
                            loading="lazy"
                        />
                        <CornerAccents />
                        <div className={`${MONO} text-[10px] uppercase text-slate-500 mt-4 text-center tracking-widest`}>
                            [ LIVE ANALYSIS VIEW ]
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    const ref = useRevealOnScroll();
    const features = [
        { icon: ShieldCheck, title: "Verifica S/MIME reale", cat: "CORE",
            text: "Trust List AgID sincronizzata giornalmente, verifica catena certificati e revoche." },
        { icon: Brain, title: "Analisi AI strutturata", cat: "AI",
            text: "Estrazione automatica di intent, IBAN, importi, deadline, entità nominate." },
        { icon: GitBranch, title: "Motore regole visuale", cat: "WORKFLOW",
            text: "Rule builder drag-and-drop con dry-run su PEC reali prima di attivare." },
        { icon: KanbanSquare, title: "Coda revisioni", cat: "WORKFLOW",
            text: "Workflow completo con assegnazione, note, escalation, false positive." },
        { icon: FileText, title: "Report PDF professionali", cat: "COMPLIANCE",
            text: "Un report per PEC + aggregati periodici, tutti brandizzati." },
        { icon: Mail, title: "Integrazione IMAP", cat: "CORE",
            text: "Connetti caselle PEC in read-only con preset per Aruba, InfoCert, Namirial, Poste." },
        { icon: Users, title: "Multi-azienda", cat: "CORE",
            text: "Isolamento tenant stretto, ruoli granulari (Amministratore/Analista/Consultazione)." },
        { icon: Lock, title: "Audit immutabile", cat: "COMPLIANCE",
            text: "Ogni azione tracciata: chi, cosa, quando, IP. Log inalterabile per compliance." },
        { icon: Cog, title: "Configurazione autonoma", cat: "WORKFLOW",
            text: "Gestori PEC, domini fidati, retention, MFA — tutto configurabile via UI." },
    ];
    return (
        <section id="funzionalita" ref={ref} className="reveal bg-[#142340] py-20 md:py-28" data-testid="section-features">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className={EYEBROW}>[ CAPABILITIES ]</div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                        Una piattaforma completa per il<br />
                        <span className="text-cyan-400">ciclo di vita della PEC</span>
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {features.map((f, i) => (
                        <div key={i}
                            data-testid={`feature-card-${i}`}
                            className="landing-feature group relative p-6 bg-[#0B1633] rounded-xl border border-cyan-400/15 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(56,189,248,0.15)] hover:border-cyan-400/40 transition-all"
                        >
                            <div className="w-11 h-11 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mb-4">
                                <f.icon className="w-5 h-5 text-cyan-400" strokeWidth={2} />
                            </div>
                            <h3 className="text-[15px] font-bold text-white mb-2">{f.title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
                            <div className={`${MONO} text-[9px] uppercase text-cyan-500/80 mt-5 pt-3 border-t border-cyan-400/10`}>
                                [ {f.cat} ]
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function PipelineSection() {
    const ref = useRevealOnScroll();
    const steps = [
        "Acquisizione", "Parsing intestazioni", "Verifica struttura PEC",
        "Verifica firma e certificato", "Identificazione mittente",
        "Analisi contenuto", "Analisi link", "Analisi allegati",
        "Calcolo del rischio", "Generazione raccomandazioni",
    ];
    const kpis = [
        { label: "THREATS", n: "34", color: "text-red-400" },
        { label: "VERIFIED", n: "1.247", color: "text-emerald-400" },
        { label: "FLAGGED", n: "23", color: "text-amber-400" },
        { label: "IN QUEUE", n: "8", color: "text-cyan-400" },
    ];
    return (
        <section ref={ref} className="reveal bg-[#0B1633] py-20 md:py-28" data-testid="section-pipeline">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className={EYEBROW}>[ PIPELINE ]</div>
                        <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                            Ogni PEC attraversa <span className="text-cyan-400">10 step</span> di verifica
                        </h2>
                        <p className="mt-5 text-base text-slate-400 max-w-xl">
                            Nessuna scorciatoia. Ogni fase produce evidenze tracciabili e revisionabili.
                        </p>
                        <ol className="mt-8 space-y-1.5" data-testid="pipeline-steps">
                            {steps.map((s, i) => (
                                <li key={i}
                                    data-testid={`pipeline-step-${i}`}
                                    className="grid grid-cols-[36px_1fr_20px] items-center gap-3 rounded-md py-2 px-3 hover:bg-white/[0.02] transition-colors">
                                    <span className={`${MONO} text-xs text-cyan-400`}>{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-sm text-slate-200">{s}</span>
                                    <ChevronRight className="w-4 h-4 text-cyan-500/40" />
                                </li>
                            ))}
                        </ol>
                        <div className={`${MONO} text-[10px] uppercase text-cyan-500/80 mt-6 tracking-widest`}>
                            [ ~ 3 SECONDI PER PEC ]
                        </div>
                    </div>
                    <div className="relative">
                        <img
                            src={IMG_NETWORK_FLOW}
                            alt="Processing flow diagram"
                            data-testid="pipeline-mockup"
                            className="rounded-2xl w-full ring-1 ring-cyan-400/15 shadow-[0_30px_100px_rgba(6,16,42,0.7)]"
                            loading="lazy"
                        />
                        <CornerAccents />
                        <div className={`${MONO} text-[10px] uppercase text-slate-500 mt-4 text-center tracking-widest`}>
                            [ PROCESSING FLOW ]
                        </div>
                    </div>
                </div>

                <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="soc-kpis">
                    {kpis.map((k) => (
                        <div key={k.label}
                            data-testid={`soc-kpi-${k.label.toLowerCase().replace(" ", "-")}`}
                            className="rounded-xl border border-cyan-400/15 bg-[#06102A] p-5 relative overflow-hidden">
                            <div className={`${MONO} text-[10px] uppercase text-slate-500 tracking-widest`}>
                                [ {k.label} ]
                            </div>
                            <div className={`${MONO} text-4xl font-bold ${k.color} mt-2`}>{k.n}</div>
                            <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function DarkDashboardShowcase() {
    const ref = useRevealOnScroll();
    return (
        <section ref={ref} className="reveal bg-[#06102A] py-20 md:py-28" data-testid="section-dashboard-showcase">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <div className={EYEBROW}>[ CONSOLE SOC ]</div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                        Vista unica su tutto il perimetro PEC
                    </h2>
                    <p className="mt-5 text-base text-slate-400">
                        Un solo pannello per il tuo team di sicurezza. Threat intelligence, code revisioni,
                        alert operativi e audit trail in un&apos;unica esperienza.
                    </p>
                </div>
                <div className="relative max-w-5xl mx-auto">
                    <img
                        src={IMG_DARK_DASHBOARD}
                        alt="Console SOC IH PEC Guard"
                        data-testid="dashboard-showcase-mockup"
                        className="rounded-2xl w-full ring-1 ring-cyan-400/20 shadow-[0_40px_140px_rgba(56,189,248,0.15)]"
                        loading="lazy"
                    />
                    <CornerAccents />
                </div>
            </div>
        </section>
    );
}

function TrustSection() {
    const ref = useRevealOnScroll();
    const cards = [
        { icon: Shield, title: "Isolamento tenant stretto", cat: "ARCHITECTURE",
            text: "I dati di ogni azienda restano isolati. Verificato con test automatici cross-tenant su ogni endpoint." },
        { icon: Fingerprint, title: "MFA + audit immutabile", cat: "AUTH",
            text: "Autenticazione a due fattori TOTP, sessioni revocabili, log inalterabile." },
        { icon: Eye, title: "Trasparenza totale sull'AI", cat: "AI",
            text: "I contenuti delle PEC non vengono usati per il training di modelli AI. Redazione dati sensibili configurabile." },
        { icon: FileCheck2, title: "GDPR ready", cat: "REGULATORY",
            text: "Retention configurabile per azienda, export dati utente, cancellazione automatica." },
    ];
    return (
        <section id="trust" ref={ref} className="reveal bg-[#06102A] py-20 md:py-28 relative" data-testid="section-trust">
            <div aria-hidden className="absolute inset-0" style={DOT_GRID_STYLE} />
            <div className="max-w-6xl mx-auto px-5 md:px-8 relative">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className={EYEBROW}>[ TRUST · COMPLIANCE · TRANSPARENCY ]</div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                        Costruita per il tuo team di<br />
                        <span className="text-cyan-400">compliance</span>
                    </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {cards.map((c, i) => (
                        <div key={i}
                            data-testid={`trust-card-${i}`}
                            className="p-6 rounded-xl border border-cyan-400/12 bg-white/[0.03] hover:bg-white/[0.06] hover:border-cyan-400/30 transition-all">
                            <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(56,189,248,0.15)]">
                                <c.icon className="w-5.5 h-5.5 text-cyan-400" strokeWidth={2} />
                            </div>
                            <h3 className="text-[15px] font-bold text-white mb-2">{c.title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
                            <div className={`${MONO} text-[9px] uppercase text-cyan-500/70 mt-5 pt-3 border-t border-cyan-400/10`}>
                                [ {c.cat} ]
                            </div>
                        </div>
                    ))}
                </div>
                <p className={`${MONO} text-[11px] text-slate-500 italic text-center mt-12 max-w-3xl mx-auto tracking-wide`}
                    data-testid="trust-disclaimer">
                    &gt; IH PEC Guard fornisce supporto tecnico e operativo. Le analisi non costituiscono un parere
                    legale e non sostituiscono la verifica umana.
                </p>
            </div>
        </section>
    );
}

function TeamSection() {
    const ref = useRevealOnScroll();
    return (
        <section ref={ref} className="reveal bg-[#142340] py-20 md:py-28" data-testid="section-team">
            <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
                <div>
                    <div className={EYEBROW}>[ ABOUT PTK SOLUTIONS ]</div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                        Ptk Solutions
                    </h2>
                    {/* TODO Andrea: sostituire testi placeholder con contenuti definitivi */}
                    <div className="mt-6 pl-4 border-l-2 border-cyan-400/50">
                        <p className="text-base text-slate-300 leading-relaxed">
                            Ptk Solutions è una software house italiana specializzata in cybersecurity, compliance
                            e automazione per aziende del gruppo e PMI. Progettiamo soluzioni enterprise che riducono
                            il rischio operativo e semplificano il lavoro dei team amministrativi e di sicurezza.
                        </p>
                        <p className="mt-4 text-base text-slate-300 leading-relaxed">
                            IH PEC Guard nasce dall&apos;esperienza diretta con clienti che hanno subito frodi tramite
                            comunicazioni PEC tecnicamente valide.
                        </p>
                    </div>
                </div>
                <div className="relative">
                    <div className="p-8 md:p-10 rounded-2xl bg-[#0B1633] border border-cyan-400/15 relative">
                        <div className="absolute -top-3 -left-3 w-12 h-12 rounded-lg bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center text-cyan-300 font-serif text-3xl leading-none">
                            &ldquo;
                        </div>
                        <blockquote className="text-lg md:text-2xl text-white font-medium leading-snug tracking-tight">
                            La sicurezza inizia dalla verifica di ciò che sembra <span className="text-cyan-400">già affidabile</span>.
                        </blockquote>
                        <div className="mt-6 pt-5 border-t border-cyan-400/15 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500" />
                            <div>
                                <div className="font-semibold text-white text-sm">Andrea Bazzarello</div>
                                <div className={`${MONO} text-[10px] uppercase text-cyan-400/80`}>Founder · Ptk Solutions</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CTASection() {
    const ref = useRevealOnScroll();
    return (
        <section ref={ref} className="reveal py-24 md:py-28 relative overflow-hidden" data-testid="section-cta">
            <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, #06102A 0%, #0B1633 100%)" }} />
            <div aria-hidden className="absolute inset-0" style={DOT_GRID_STYLE} />
            <div
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(56,189,248,0.16), transparent 55%)" }}
            />
            <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
                <div className={EYEBROW}>[ READY FOR DEPLOYMENT ]</div>
                <h2 className="mt-5 text-4xl md:text-6xl font-black text-white leading-tight tracking-tight">
                    Proteggi il tuo perimetro PEC. <span className="text-cyan-400">Oggi.</span>
                </h2>
                <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
                    Setup in meno di un giorno. Nessun servizio esterno a pagamento.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                        href={DEMO_MAILTO}
                        data-testid="cta-final-demo"
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-white font-semibold hover:shadow-[0_0_60px_rgba(56,189,248,0.55)] transition-all"
                        style={{ background: "linear-gradient(90deg, #38BDF8, #2563EB)" }}
                    >
                        Richiedi demo
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <Link
                        to="/login"
                        data-testid="cta-final-login"
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border border-cyan-400/40 text-cyan-100 hover:bg-cyan-400/10 hover:border-cyan-400 font-semibold transition-all"
                    >
                        Accedi
                    </Link>
                </div>
                <p className={`${MONO} text-[10px] uppercase text-slate-500 mt-6 tracking-widest`}>
                    [ mailto: andrea.bazzarello@ptkserv.it ]
                </p>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer id="contatti" className="bg-[#06102A] border-t border-cyan-400/15 pt-14 pb-8 text-slate-400" data-testid="landing-footer">
            <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-10">
                <div>
                    <div className="flex items-center gap-2.5 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shadow-[0_0_20px_rgba(56,189,248,0.15)]">
                            <ShieldCheck className="w-5 h-5 text-cyan-400" strokeWidth={2.25} />
                        </div>
                        <span className="text-[15px] font-bold text-white tracking-tight">IH PEC Guard</span>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
                        La difesa che vede oltre la firma.
                    </p>
                    <p className={`${MONO} text-[10px] text-slate-600 mt-5 uppercase`}>
                        © 2026 PTK SOLUTIONS · TUTTI I DIRITTI RISERVATI
                    </p>
                </div>
                <div>
                    <div className={`${MONO} text-[10px] uppercase text-cyan-400/80 mb-4 tracking-widest`}>[ DOCUMENTI ]</div>
                    <ul className="space-y-2 text-sm">
                        <li><a href="#" title="In preparazione" className="hover:text-cyan-300 transition-colors">Privacy</a></li>
                        <li><a href="#" title="In preparazione" className="hover:text-cyan-300 transition-colors">Termini</a></li>
                        <li><a href="#" title="In preparazione" className="hover:text-cyan-300 transition-colors">Trust &amp; Compliance</a></li>
                    </ul>
                </div>
                <div>
                    <div className={`${MONO} text-[10px] uppercase text-cyan-400/80 mb-4 tracking-widest`}>[ CONTATTI ]</div>
                    <ul className="space-y-2 text-sm">
                        <li>
                            <a href="mailto:andrea.bazzarello@ptkserv.it"
                                data-testid="footer-mailto"
                                className="hover:text-cyan-300 transition-colors">
                                andrea.bazzarello@ptkserv.it
                            </a>
                        </li>
                        <li className="text-slate-500">Italia</li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-cyan-400/10 mt-10 pt-6 text-center">
                <p className={`${MONO} text-[10px] uppercase text-slate-600`}>
                    Creato da <span className="text-cyan-300 font-semibold">Ptk Solutions</span>
                </p>
            </div>
        </footer>
    );
}

export default function LandingPage() {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = "IH PEC Guard — Cybersecurity per PEC italiane | Ptk Solutions";
        const setMeta = (attr, key, value) => {
            let el = document.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
        };
        setMeta("name", "description",
            "Piattaforma enterprise per verificare l'autenticità delle PEC, rilevare frodi, phishing e richieste sospette di variazione IBAN. Trust List AgID integrata.");
        setMeta("property", "og:title", "IH PEC Guard — La difesa che vede oltre la firma");
        setMeta("property", "og:description",
            "Verifica firma S/MIME, Trust List AgID, analisi AI del contenuto e motore regole per proteggere le PEC aziendali.");
        setMeta("property", "og:image", IMG_DARK_DASHBOARD);
        setMeta("property", "og:type", "website");
        setMeta("name", "twitter:card", "summary_large_image");
        return () => { document.title = prevTitle; };
    }, []);

    return (
        <div
            className="min-h-screen font-sans text-white"
            style={{ background: "linear-gradient(180deg, #06102A 0%, #0B1633 45%, #0B1633 100%)" }}
            data-testid="landing-page"
        >
            <NavBar />
            <main>
                <Hero />
                <ProblemSection />
                <DimensionsSection />
                <FeaturesSection />
                <PipelineSection />
                <DarkDashboardShowcase />
                <TrustSection />
                <TeamSection />
                <CTASection />
            </main>
            <Footer />
        </div>
    );
}
